import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  MenuItem,
  Alert,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  LibraryAdd as LibraryAddIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { coursesAPI, modulesAPI, lessonsAPI, testsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import JsonImportExport from '../components/UIKit/JsonImportExport';
import Button from '../components/UIKit/Button';
import TextField from '../components/UIKit/TextField';
import Select from '../components/UIKit/Select';
import Card from '../components/UIKit/Card';
import CustomAutocomplete from '../components/UIKit/CustomAutocomplete';
import { useTranslation } from 'react-i18next';
import libraryEventsService from '../services/libraryEventsService';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProfessions, selectAllProfessions, selectProfessionsLoading } from '../redux/slices/professionsSlice';
import { fetchTools, selectAllTools, selectToolsLoading } from '../redux/slices/toolsSlice';
import { fetchSkills } from '../redux/slices/skillsSlice';
import LessonSelector from '../components/courses/LessonSelector';
import ModuleSelector from '../components/courses/ModuleSelector';
import ModuleCard from '../components/courses/ModuleCard';
import HeroSection from '../components/courses/HeroSection';

const CourseCreate = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const dispatch = useDispatch();
  const { t } = useTranslation(['courses', 'common']);
  const [loading, setLoading] = useState(false);
  const [expandedModules, setExpandedModules] = useState({});
  const [localModuleFields, setLocalModuleFields] = useState({}); 

  // Используем данные из Redux вместо локального состояния
  const professions = useSelector(selectAllProfessions);
  const professionsLoading = useSelector(selectProfessionsLoading);
  const [selectedProfessions, setSelectedProfessions] = useState([]);
  const [manualProfessions, setManualProfessions] = useState(new Set()); // Профессии, добавленные вручную

  const tools = useSelector(selectAllTools);
  const toolsLoading = useSelector(selectToolsLoading);
  const [selectedTools, setSelectedTools] = useState([]);

  // Состояние для формы создания курса
  const [courseData, setCourseData] = useState({
    title: "",
    description: "",
    difficulty: "beginner",
    image: null,
    videoUrl: "", // Single video URL field
    professions: [],
    tools: [],
    modules: [], // Начинаем с пустого массива модулей
  });
  
  const [isDraft, setIsDraft] = useState(true); // Draft by default
  const [heroPreviewOpen, setHeroPreviewOpen] = useState(false); // For hero preview
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);

  // Функция для вычисления статистики курса для превью
  const calculatePreviewStats = () => {
    let totalDuration = 0;
    let lessonCount = 0;
    let testCount = 0;
    let presentationCount = 0;
    const videoContent = [];

    courseData.modules.forEach(module => {
      if (module.lessons && Array.isArray(module.lessons)) {
        module.lessons.forEach(lesson => {
          lessonCount++;
          totalDuration += lesson.duration || 15;
          
          // Проверяем наличие видео
          if (lesson.videoUrl) {
            videoContent.push(lesson.videoUrl);
          }
          
          // Проверяем тип контента (presentation)
          if (lesson.contentType === 'presentation') {
            presentationCount++;
          }
          
          // Считаем тесты в уроке
          if (lesson.tests && Array.isArray(lesson.tests)) {
            testCount += lesson.tests.length;
          }
        });
      }
    });

    // Проверяем основное видео курса
    if (courseData.videoUrl) {
      videoContent.push(courseData.videoUrl);
    }

    return {
      duration: totalDuration,
      lessonCount,
      testCount,
      videoContent,
      presentationCount,
      totalStudents: 0, // Для превью всегда 0
    };
  };

  // Внутри компонента CourseCreate добавляем состояние для управления диалогом выбора уроков
  const [lessonSelectorOpen, setLessonSelectorOpen] = useState(false);
  const [moduleSelectorOpen, setModuleSelectorOpen] = useState(false);
  const [currentModuleIndex, setCurrentModuleIndex] = useState(null);

  // Загрузка профессий и инструментов из Redux при монтировании компонента
  useEffect(() => {
    dispatch(fetchProfessions({ all: true, isCommon:1 }));
    dispatch(fetchTools({ all: true, isCommon: 1 }));
    dispatch(fetchSkills({ all: true, isCommon: 1 }));
  }, [dispatch]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusDropdownOpen) {
        setStatusDropdownOpen(false);
      }
    };

    if (statusDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [statusDropdownOpen]);

  // Обработка событий удаления профессий из выбранных
  useEffect(() => {
    // Если профессия была удалена, нужно удалить её из выбранных
    const handleProfessionEvent = (action, data) => {
      if (action === 'delete' && data && data.id) {
        setSelectedProfessions(prevSelected => 
          prevSelected.filter(p => p.id !== data.id)
        );
        
        // Также обновляем данные курса
        setCourseData(prevData => ({
          ...prevData,
          professions: prevData.professions.filter(id => id !== data.id)
        }));
      }
    };
    
    // Обработчик событий инструментов
    const handleToolEvent = (action, data) => {
      if (action === 'delete' && data && data.id) {
        setSelectedTools(prevSelected =>
          prevSelected.filter(t => t.id !== data.id)
        );

        // Также обновляем данные курса
        setCourseData(prevData => ({
          ...prevData,
          tools: prevData.tools.filter(id => id !== data.id)
        }));
      }
    };

    // Подписываемся на события профессий и инструментов
    const unsubscribeProfession = libraryEventsService.subscribe('profession', handleProfessionEvent);
    const unsubscribeTool = libraryEventsService.subscribe('tool', handleToolEvent);

    // Отписываемся при размонтировании компонента
    return () => {
      unsubscribeProfession();
      unsubscribeTool();
    };
  }, []);


  // Функция для синхронизации профессий курса с профессиями уроков
  const syncProfessionsFromLessons = useCallback(() => {
    setCourseData(prevData => {
      // Собираем все профессии из всех уроков всех модулей
      const allLessonProfessions = new Set();
      
      prevData.modules.forEach(module => {
        if (module.lessons && Array.isArray(module.lessons)) {
          module.lessons.forEach(lesson => {
            if (lesson.professions && Array.isArray(lesson.professions)) {
              lesson.professions.forEach(profId => {
                // Добавляем ID профессии (может быть строкой или объектом)
                const id = typeof profId === 'object' ? (profId.id || profId._id) : profId;
                if (id) {
                  allLessonProfessions.add(String(id));
                }
              });
            }
          });
        }
      });

      // Объединяем профессии из уроков с вручную добавленными профессиями
      const mergedProfessions = new Set([
        ...Array.from(manualProfessions), 
        ...Array.from(allLessonProfessions)
      ]);
      
      const mergedProfessionsArray = Array.from(mergedProfessions);
      const currentCourseProfessions = (prevData.professions || []).map(p => String(p));
      
      // Проверяем, изменились ли профессии
      const professionsChanged = 
        mergedProfessionsArray.length !== currentCourseProfessions.length ||
        !mergedProfessionsArray.every(id => currentCourseProfessions.includes(id)) ||
        !currentCourseProfessions.every(id => mergedProfessionsArray.includes(id));
      
      if (professionsChanged) {
        // Обновляем selectedProfessions - находим полные объекты профессий
        const updatedSelectedProfessions = professions.filter(prof => 
          mergedProfessionsArray.includes(String(prof.id || prof._id))
        );
        
        // Используем setTimeout чтобы обновить selectedProfessions после обновления state
        setTimeout(() => {
          setSelectedProfessions(updatedSelectedProfessions);
        }, 0);
        
        // Возвращаем обновленные данные курса
        return {
          ...prevData,
          professions: mergedProfessionsArray
        };
      }
      
      // Если ничего не изменилось, возвращаем предыдущее состояние
      return prevData;
    });
  }, [professions, manualProfessions]);

  // Автоматическая синхронизация профессий при изменении модулей
  useEffect(() => {
    // Синхронизируем профессии только если есть модули с уроками
    const hasLessons = courseData.modules.some(module => 
      module.lessons && Array.isArray(module.lessons) && module.lessons.length > 0
    );
    
    if (hasLessons) {
      syncProfessionsFromLessons();
    }
  }, [courseData.modules, syncProfessionsFromLessons]); // Зависимость от модулей и функции синхронизации

  // Обработчик выбора инструментов
  const handleToolsChange = useCallback((newValue) => {
    // Проверяем что newValue определен и является массивом
    if (!newValue || !Array.isArray(newValue)) {
      console.warn('handleToolsChange: newValue is not an array', newValue);
      return;
    }

    // Обновляем выбранные инструменты
    setSelectedTools(newValue);

    // Обновляем данные курса
    setCourseData(prevData => ({
      ...prevData,
      tools: newValue.map((tool) => tool.id),
    }));
  }, []);

  // Обработчик выбора профессий
  const handleProfessionsChange = useCallback((newValue) => {
    // Проверяем что newValue определен и является массивом
    if (!newValue || !Array.isArray(newValue)) {
      console.warn('handleProfessionsChange: newValue is not an array', newValue);
      return;
    }

    setSelectedProfessions(newValue);

    // Обновляем данные курса
    setCourseData(prevData => ({
      ...prevData,
      professions: newValue.map(p => p.id || p._id),
    }));

    // Определяем какие профессии из уроков
    const lessonProfessionIds = new Set();
    courseData.modules.forEach(module => {
      if (module.lessons && Array.isArray(module.lessons)) {
        module.lessons.forEach(lesson => {
          if (lesson.professions && Array.isArray(lesson.professions)) {
            lesson.professions.forEach(profId => {
              const id = typeof profId === 'object' ? (profId.id || profId._id) : profId;
              if (id) lessonProfessionIds.add(String(id));
            });
          }
        });
      }
    });

    // Вручную добавленные = те что выбраны, но НЕТ в уроках
    const manualOnly = newValue
      .map(p => String(p.id || p._id))
      .filter(id => !lessonProfessionIds.has(id));
    
    setManualProfessions(new Set(manualOnly));
  }, [courseData.modules]);

  // Оптимизированные обработчики событий
  const handleCourseDataChange = useCallback((e) => {
    const { name, value } = e.target;
    setCourseData(prevData => ({
      ...prevData,
      [name]: value,
    }));
  }, []);

  // Обработчик изменения полей модуля с debouncing
  const handleModuleFieldChange = useCallback((moduleIndex, e) => {
    const { name, value } = e.target;
    const fieldKey = `module-${moduleIndex}-${name}`;
    
    // Обновляем локальное состояние модуля немедленно
    setLocalModuleFields(prev => ({
      ...prev,
      [fieldKey]: value
    }));

    // Обновляем основное состояние курса
    setCourseData(prevData => {
      const updatedModules = [...prevData.modules];
      updatedModules[moduleIndex] = {
        ...updatedModules[moduleIndex],
        [name]: value,
      };
      return {
        ...prevData,
        modules: updatedModules,
      };
    });
  }, []);

  // Обработчик для управления раскрытием аккордеонов модулей
  const handleToggleExpanded = useCallback((moduleIndex, isExpanded) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleIndex]: isExpanded
    }));
  }, []);

  // Обработчик изменения порядка модуля
  const handleModuleOrderChange = useCallback((moduleIndex, newOrder) => {
    setCourseData(prevData => {
      const updatedModules = [...prevData.modules];
      updatedModules[moduleIndex] = {
        ...updatedModules[moduleIndex],
        order: newOrder,
      };
      return {
        ...prevData,
        modules: updatedModules,
      };
    });
  }, []);

  const handleImageUpload = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setCourseData(prevData => ({
          ...prevData,
          image: event.target.result,
          imagePreview: event.target.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const handleRemoveImage = useCallback(() => {
    setCourseData(prevData => ({
      ...prevData,
      image: '',
      imagePreview: '',
    }));
  }, []);

  const handleVideoUrlChange = useCallback((e) => {
    setCourseData(prevData => ({
      ...prevData,
      videoUrl: e.target.value,
    }));
  }, []);

  // Оптимизированные функции для работы с модулями
  const addModule = useCallback(() => {
    setCourseData(prevData => {
      const newModuleIndex = prevData.modules.length;
      
      // Добавляем локальные состояния для нового модуля
      setLocalModuleFields(prev => ({
        ...prev,
        [`module-${newModuleIndex}-title`]: '',
        [`module-${newModuleIndex}-description`]: '',
        [`module-${newModuleIndex}-content`]: '',
      }));

      return {
        ...prevData,
        modules: [
          ...prevData.modules,
          {
            title: '',
            description: '',
            content: '',
            lessons: [
              {
                title: '',
                content: '',
                duration: 15,
                type: 'text',
                contentType: 'standard',
                resources: [],
                practiceExercises: [],
                contentBlocks: [],
                professions: [],
                tests: [], // Тесты теперь находятся внутри урока
              },
            ],
          },
        ],
      };
    });
  }, []);

  const removeModule = useCallback((index) => {
    // Удаляем локальные состояния для удаляемого модуля
    setLocalModuleFields(prev => {
      const updated = { ...prev };
      delete updated[`module-${index}-title`];
      delete updated[`module-${index}-description`];
      delete updated[`module-${index}-content`];
      
      // Перенумеровываем локальные состояния для модулей, которые идут после удаляемого
      const reindexed = {};
      Object.keys(updated).forEach(key => {
        const match = key.match(/^module-(\d+)-(.+)$/);
        if (match) {
          const moduleIndex = parseInt(match[1]);
          const fieldName = match[2];
          
          if (moduleIndex > index) {
            // Сдвигаем индекс на единицу назад
            reindexed[`module-${moduleIndex - 1}-${fieldName}`] = updated[key];
          } else {
            reindexed[key] = updated[key];
          }
        } else {
          reindexed[key] = updated[key];
        }
      });
      
      return reindexed;
    });

    // Обновляем состояние раскрытых модулей
    setExpandedModules(prev => {
      const updated = { ...prev };
      delete updated[index];
      
      // Перенумеровываем индексы для модулей, которые идут после удаляемого
      const reindexed = {};
      Object.keys(updated).forEach(key => {
        const moduleIndex = parseInt(key);
        if (moduleIndex > index) {
          reindexed[moduleIndex - 1] = updated[key];
        } else {
          reindexed[key] = updated[key];
        }
      });
      
      return reindexed;
    });

    setCourseData(prevData => {
      const updatedModules = [...prevData.modules];
      updatedModules.splice(index, 1);
      return {
        ...prevData,
        modules: updatedModules,
      };
    });
  }, []);

  const addLesson = useCallback((moduleIndex) => {
    setCourseData(prevData => {
      const updatedModules = [...prevData.modules];
    updatedModules[moduleIndex].lessons.push({
      title: '',
      content: '',
      duration: 15,
      type: 'text',
      contentType: 'standard',
      contentBlocks: [],
      resources: [],
      practiceExercises: [],
      professions: [],
      tests: [], // Тесты теперь находятся внутри урока
    });
      return {
        ...prevData,
      modules: updatedModules,
  };
    });
  }, []);


  // Оптимизированные обработчики для кнопок модулей
  const handleAddLesson = useCallback((moduleIndex) => {
    addLesson(moduleIndex);
  }, [addLesson]);

  const handleRemoveModule = useCallback((moduleIndex) => {
    removeModule(moduleIndex);
  }, [removeModule]);

  // Обработчик изменений в уроках модуля
  const handleLessonChange = useCallback((moduleIndex, action, data, lessonIndex = null) => {
    setCourseData(prevData => {
      const updatedModules = [...prevData.modules];
      const currentLessons = [...updatedModules[moduleIndex].lessons];

      switch (action) {
        case 'addArray':
          currentLessons.push(...data);
          break;
        case 'update':
          if (lessonIndex !== null) {
            currentLessons[lessonIndex] = data;
          }
          break;
        case 'remove':
          if (lessonIndex !== null) {
            currentLessons.splice(lessonIndex, 1);
          }
          break;
        default:
          break;
      }

      updatedModules[moduleIndex] = {
        ...updatedModules[moduleIndex],
        lessons: currentLessons
      };

      return {
        ...prevData,
        modules: updatedModules
      };
    });

    // Синхронизация происходит автоматически через useEffect при изменении модулей
  }, []);


  // Обработчики для превью HeroSection
  const formatDuration = (minutes, translationFn) => {
    if (!minutes) return 'N/A';

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours === 0) {
      return `${remainingMinutes} ${translationFn('common:time.shortMin')}`;
    } else if (remainingMinutes === 0) {
      return `${hours} ${translationFn('common:time.shortHour')}`;
    } else {
      return `${hours} ${translationFn('common:time.shortHour')} ${remainingMinutes} ${translationFn('common:time.shortMin')}`;
    }
  };

  const handleHeroPreviewToggleBookmark = () => {
    // Ничего не делаем в превью
  };

  const handleHeroPreviewContinueLearning = () => {
    // Ничего не делаем в превью
  };

  const handleHeroPreviewEnroll = () => {
    // Ничего не делаем в превью
  };

  const handleHeroPreviewScrollToReviews = () => {
    // Ничего не делаем в превью
  };

  // Подготовка данных для HeroSection превью
  const getPreviewCourseData = () => {
    // Приоритет: imagePreview (data URL) > image (URL или путь)
    let imageUrl = '';
    
    if (courseData.imagePreview) {
      // Используем preview изображение (data URL)
      imageUrl = courseData.imagePreview;
    } else if (courseData.image) {
      // Используем основное изображение
      imageUrl = courseData.image;
    }
    
    const previewData = {
      title: courseData.title || 'Course Title',
      description: courseData.description || 'Course description...',
      difficulty: courseData.difficulty || 'beginner',
      image: imageUrl,
      isDraft: isDraft,
      professions: selectedProfessions,
      tools: selectedTools,
      // Добавляем категорию для дефолтного изображения
      category: courseData.category || { slug: 'default' },
    };
    
    // Debug: выводим в консоль для проверки
    console.log('Preview course data:', {
      hasImage: !!imageUrl,
      imageUrl: imageUrl ? (imageUrl.substring(0, 50) + '...') : 'empty',
      title: previewData.title,
    });
    
    return previewData;
  };

  // Проверка прав доступа
  if (!user) {
    return (
      <Container maxWidth="md">
        <Box sx={{ my: 4 }}>
          <Alert severity="error">{t('courses:courseCreate.ui.noAccess')}</Alert>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/courses')}
            sx={{ mt: 2 }}
          >
            {t('courses:courseCreate.ui.backToCourses')}
          </Button>
        </Box>
      </Container>
    );
  }

  // Функция для обработки выбора существующих модулей
  const handleSelectExistingModules = async (selectedModuleIds) => {
    try {
      // Загружаем данные выбранных модулей
      const modulePromises = selectedModuleIds.map(moduleId =>
        modulesAPI.getById(moduleId)
      );
      const moduleResponses = await Promise.all(modulePromises);
      const selectedModules = moduleResponses.map(response => response.data);

      // Добавляем модули в курс с правильной структурой
      setCourseData(prevData => {
        const newModules = selectedModules.map((module, index) => {
          // Обрабатываем уроки модуля
          const lessons = (module.lessons || []).map(lesson => {
            // Если lesson - это объект с данными урока
            if (typeof lesson === 'object' && lesson._id) {
              return {
                _id: lesson._id,
                title: lesson.title || '',
                description: lesson.description || '',
                content: lesson.content || '',
                duration: lesson.duration || 15,
                type: lesson.type || 'text',
                contentType: lesson.contentType || 'standard',
                contentBlocks: lesson.contentBlocks || [],
                resources: lesson.resources || [],
                practiceExercises: lesson.practiceExercises || [],
                professions: lesson.professions || [],
                skills: lesson.skills || [],
                videoUrl: lesson.videoUrl || '',
                image: lesson.image || '',
                isDraft: lesson.isDraft || false,
                tests: lesson.tests || [] // Тесты теперь на уровне урока!
              };
            }
            // Если lesson - это только ID, возвращаем базовую структуру
            return {
              _id: lesson,
              title: '',
              content: '',
              duration: 15,
              type: 'text',
              contentType: 'standard',
              contentBlocks: [],
              resources: [],
              practiceExercises: [],
              professions: [],
              skills: [],
              videoUrl: '',
              image: '',
              isDraft: false,
              tests: []
            };
          });

          // ВАЖНО: Сохраняем _id модуля для существующего модуля
          return {
            _id: module._id, // Обязательно сохраняем ID существующего модуля!
            title: module.title || '',
            description: module.description || '',
            content: module.content || '',
            videoUrl: module.videoUrl || '',
            slug: module.slug || '', // Также сохраняем slug
            previewImage: module.previewImage || '', // Сохраняем превью изображение
            lessons: lessons,
            order: prevData.modules.length + index,
            isDraft: module.isDraft || false
          };
        });

        // Инициализируем локальные поля для новых модулей
        const newLocalFields = {};
        newModules.forEach((module, index) => {
          const moduleIndex = prevData.modules.length + index;
          newLocalFields[`module-${moduleIndex}-title`] = module.title || '';
          newLocalFields[`module-${moduleIndex}-description`] = module.description || '';
          newLocalFields[`module-${moduleIndex}-content`] = module.content || '';
        });

        setLocalModuleFields(prev => ({
          ...prev,
          ...newLocalFields
        }));

        return {
          ...prevData,
          modules: [
            ...prevData.modules,
            ...newModules
          ]
        };
      });

      toast.success(`Добавлено ${selectedModules.length} модулей в курс`);
    } catch (error) {
      console.error('Ошибка при добавлении модулей:', error);
      toast.error('Ошибка при добавлении модулей');
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Проверка данных перед отправкой
      if (!courseData.title.trim()) {
        throw new Error(t('courses:courseCreate.validation.titleRequired'));
      }
      if (!courseData.description.trim()) {
        throw new Error(t('courses:courseCreate.validation.descriptionRequired'));
      }
      if (!courseData.modules.length) {
        throw new Error(t('courses:courseCreate.validation.moduleRequired'));
      }

      // Проверка данных модулей и уроков
      for (let i = 0; i < courseData.modules.length; i++) {
        const module = courseData.modules[i];
        if (!module.title.trim()) {
          throw new Error(
            t('courses:courseCreate.validation.moduleTitle', { index: i + 1 })
          );
        }
        if (!module.lessons.length) {
          throw new Error(
            t('courses:courseCreate.validation.lessonRequired', { index: i + 1 })
          );
        }

        for (let j = 0; j < module.lessons.length; j++) {
          const lesson = module.lessons[j];
          if (!lesson.title.trim()) {
            throw new Error(
              t('courses:courseCreate.validation.lessonTitle', {
                lessonIndex: j + 1,
                moduleIndex: i + 1,
              })
            );
          }

          // Проверка содержимого в зависимости от типа
          if (lesson.contentType === 'mixed') {
            // Для смешанного типа проверяем наличие блоков контента
            if (!lesson.contentBlocks || lesson.contentBlocks.length === 0) {
              throw new Error(
                t('courses:courseCreate.validation.contentBlockRequired', {
                  lessonIndex: j + 1,
                  moduleIndex: i + 1,
                })
              );
            }

            // Проверка каждого блока на наличие содержимого
            for (let k = 0; k < lesson.contentBlocks.length; k++) {
              const block = lesson.contentBlocks[k];
              if (!block.content || block.content.trim() === '') {
                throw new Error(
                  t('courses:courseCreate.validation.blockContent', {
                    blockIndex: k + 1,
                    lessonIndex: j + 1,
                    moduleIndex: i + 1,
                  })
                );
              }
            }
          } else {
            // Для обычных типов проверяем поле content
            if (!lesson.content.trim()) {
              throw new Error(
                t('courses:courseCreate.validation.lessonContent', {
                  lessonIndex: j + 1,
                  moduleIndex: i + 1,
                })
              );
            }
          }

          // Проверка тестов внутри уроков
          for (let k = 0; k < lesson.tests?.length || 0; k++) {
            const test = lesson.tests[k];
            if (!test.title.trim()) {
              throw new Error(
                t('courses:courseCreate.validation.testTitle', {
                  testIndex: k + 1,
                  lessonIndex: j + 1,
                  moduleIndex: i + 1,
                })
              );
            }
            if (!test.questions.length) {
              throw new Error(
                t('courses:courseCreate.validation.questionRequired', {
                  testIndex: k + 1,
                  lessonIndex: j + 1,
                  moduleIndex: i + 1,
                })
              );
            }

            for (let l = 0; l < test.questions.length; l++) {
              const question = test.questions[l];
              if (!question.question.trim()) {
                throw new Error(
                  t('courses:courseCreate.validation.questionText', {
                    questionIndex: l + 1,
                    testIndex: k + 1,
                    lessonIndex: j + 1,
                    moduleIndex: i + 1,
                  })
                );
              }

              // Проверка вариантов ответа
              if (['single-choice', 'multiple-choice'].includes(question.type)) {
                if (!question.options || question.options.length < 2) {
                  throw new Error(
                    t('courses:courseCreate.validation.optionsRequired', {
                      questionIndex: l + 1,
                      testIndex: k + 1,
                      lessonIndex: j + 1,
                      moduleIndex: i + 1,
                    })
                  );
                }

                let hasCorrectOption = question.options.some(
                  (option) => option.isCorrect
                );
                if (!hasCorrectOption) {
                  throw new Error(
                    t('courses:courseCreate.validation.correctOption', {
                      questionIndex: l + 1,
                      testIndex: k + 1,
                      lessonIndex: j + 1,
                      moduleIndex: i + 1,
                    })
                  );
                }

                for (let m = 0; m < question.options.length; m++) {
                  if (!question.options[m].text.trim()) {
                    throw new Error(
                      t('courses:courseCreate.validation.optionText', {
                        optionIndex: m + 1,
                        questionIndex: l + 1,
                        testIndex: k + 1,
                        lessonIndex: j + 1,
                        moduleIndex: i + 1,
                      })
                    );
                  }
                }
              } else if (question.type === 'memory') {
                if (!question.options || question.options.length < 4) {
                  throw new Error(
                    t('courses:courseCreate.validation.memoryCards', {
                      questionIndex: l + 1,
                      testIndex: k + 1,
                      lessonIndex: j + 1,
                      moduleIndex: i + 1,
                    })
                  );
                }

                // Проверка, что каждая карточка имеет текст
                for (let m = 0; m < question.options.length; m++) {
                  if (!question.options[m].text.trim()) {
                    throw new Error(
                      t('courses:courseCreate.validation.cardText', {
                        cardIndex: m + 1,
                        questionIndex: l + 1,
                        testIndex: k + 1,
                        lessonIndex: j + 1,
                        moduleIndex: i + 1,
                      })
                    );
                  }
                }

                // Проверка, что каждая карточка имеет pairId
                for (let m = 0; m < question.options.length; m++) {
                  if (!question.options[m].pairId) {
                    throw new Error(
                      t('courses:courseCreate.validation.cardPairId', {
                        cardIndex: m + 1,
                        questionIndex: l + 1,
                        testIndex: k + 1,
                        lessonIndex: j + 1,
                        moduleIndex: i + 1,
                      })
                    );
                  }
                }

                // Проверка, что для каждого pairId есть ровно 2 карточки
                const pairCounts = {};
                for (let m = 0; m < question.options.length; m++) {
                  const pairId = question.options[m].pairId;
                  pairCounts[pairId] = (pairCounts[pairId] || 0) + 1;
                }

                const invalidPairs = Object.entries(pairCounts).filter(
                  ([_, count]) => count !== 2
                );
                if (invalidPairs.length > 0) {
                  throw new Error(
                    t('courses:courseCreate.validation.invalidPairs', {
                      questionIndex: l + 1,
                      testIndex: k + 1,
                      lessonIndex: j + 1,
                      moduleIndex: i + 1,
                    })
                  );
                }
              } else if (question.type === 'true-false') {
                // Проверка для типа true-false
                if (
                  !question.correctAnswer ||
                  !['true', 'false'].includes(question.correctAnswer)
                ) {
                  throw new Error(
                    t('courses:courseCreate.validation.trueFalseAnswer', {
                      questionIndex: l + 1,
                      testIndex: k + 1,
                      lessonIndex: j + 1,
                      moduleIndex: i + 1,
                    })
                  );
                }
              } else if (question.type === 'text') {
                // Проверка для текстового типа
                if (!question.correctAnswer || !question.correctAnswer.trim()) {
                  throw new Error(
                    t('courses:courseCreate.validation.textAnswer', {
                      questionIndex: l + 1,
                      testIndex: k + 1,
                      lessonIndex: j + 1,
                      moduleIndex: i + 1,
                    })
                  );
                }
              }
            }
          }
        }
      }

      // 1. Создаем основной курс без модулей, уроков и тестов
      const baseCourseData = {
        title: courseData.title,
        description: courseData.description,
        difficulty: courseData.difficulty,
        professions: courseData.professions || [], // Добавляем профессии
        tools: courseData.tools || [], // Добавляем инструменты
        videos: courseData.videoUrl?.trim() ? [courseData.videoUrl.trim()] : [], // Добавляем видео как массив
        isDraft: isDraft,
      };

      // Удаляем поле imagePreview, оно нужно только для UI
      if (baseCourseData.imagePreview) {
        delete baseCourseData.imagePreview;
      }

      // Проверяем, загружено ли изображение в формате data URL
      const isDataURL = courseData.imagePreview && courseData.imagePreview.startsWith('data:');
      const imageToUpload = isDataURL ? courseData.imagePreview : null;

      // Сохраняем URL изображения, если это не data URL
      if (!isDataURL && courseData.image) {
        baseCourseData.image = courseData.image;
      }

      // Отправляем запрос на создание базового курса
      let courseResponse;
      if (imageToUpload) {
        // Если есть загруженное изображение, используем FormData
        const formData = new FormData();

        // Преобразуем data URL в Blob
        const imageBlob = await fetch(imageToUpload).then((r) => r.blob());
        formData.append('image', imageBlob, 'course-image.jpg');

        // Добавляем JSON с данными курса
        formData.append('data', JSON.stringify(baseCourseData));

        // Отправляем с правильными заголовками
        courseResponse = await coursesAPI.createWithImage(formData);
      } else {
        // Если изображения нет или это URL, отправляем обычным JSON
        courseResponse = await coursesAPI.create(baseCourseData);
      }


      const courseId = courseResponse.data._id;
      const courseSlug = courseResponse.data.slug;

      // 2. ПОСЛЕДОВАТЕЛЬНОЕ создание: модуль → уроки → тесты → следующий модуль
      console.log(`Начинаем создание ${courseData.modules.length} модулей последовательно...`);

      // Счетчики для отслеживания успехов и ошибок
      let totalErrors = 0;
      let totalWarnings = 0;

      for (let moduleIndex = 0; moduleIndex < courseData.modules.length; moduleIndex++) {
        const originalModule = courseData.modules[moduleIndex];

        console.log(`\n📦 Модуль ${moduleIndex + 1}/${courseData.modules.length}: "${originalModule.title}"`);

        // 2.1. Создаем или обновляем модуль
        const moduleData = {
          title: originalModule.title,
          description: originalModule.description || '',
          content: originalModule.content || '',
          videoUrl: originalModule.videoUrl || '',
          courseId: courseId,  // Передаем courseId для автоматической привязки к курсу
          order: originalModule.order || moduleIndex
        };

        let moduleId;
        try {
          let moduleResponse;

          // Проверяем, есть ли файл превью изображения для загрузки
          const hasPreviewImageFile = originalModule.previewImageFile instanceof File;

          // Если модуль существующий (выбран из библиотеки), обновляем его
          if (originalModule._id) {
            moduleId = originalModule._id;

            if (hasPreviewImageFile) {
              // Обновляем с изображением через FormData
              const formData = new FormData();
              formData.append('previewImage', originalModule.previewImageFile);
              Object.keys(moduleData).forEach(key => {
                formData.append(key, moduleData[key]);
              });
              moduleResponse = await modulesAPI.updateWithImage(moduleId, formData);
            } else {
              // Обновляем без изображения
              moduleResponse = await modulesAPI.update(moduleId, moduleData);
            }
            console.log(`  ✓ Существующий модуль обновлен и привязан к курсу с ID: ${moduleId}`);
          } else {
            // Создаем новый модуль
            if (hasPreviewImageFile) {
              // Создаем с изображением через FormData
              const formData = new FormData();
              formData.append('previewImage', originalModule.previewImageFile);
              Object.keys(moduleData).forEach(key => {
                formData.append(key, moduleData[key]);
              });
              moduleResponse = await modulesAPI.createWithImage(formData);
            } else {
              // Создаем без изображения
              moduleResponse = await modulesAPI.create(moduleData);
            }
            moduleId = moduleResponse.data.module._id;

            if (!moduleId) {
              throw new Error('Не получен ID созданного модуля');
            }

            console.log(`  ✓ Модуль создан и привязан к курсу с ID: ${moduleId}`);
          }
        } catch (error) {
          console.error(`  ✗ Ошибка при создании/обновлении модуля "${originalModule.title}":`, error);
          toast.error(`Ошибка при создании модуля "${originalModule.title}"`);
          totalErrors++;
          continue; // Переходим к следующему модулю
        }

        // 2.2. Для этого модуля создаем уроки последовательно
        console.log(`  📚 Создаем ${originalModule.lessons.length} уроков для модуля...`);

        for (let lessonIndex = 0; lessonIndex < originalModule.lessons.length; lessonIndex++) {
          const originalLesson = originalModule.lessons[lessonIndex];

          console.log(`    📖 Урок ${lessonIndex + 1}/${originalModule.lessons.length}: "${originalLesson.title}"`);

          // Базовые данные урока
          const lessonData = {
            title: originalLesson.title,
            content: originalLesson.content,
            duration: Number(originalLesson.duration) || 15,
            type: originalLesson.type || 'text',
            contentType: originalLesson.contentType || 'standard',
            contentBlocks: originalLesson.contentBlocks || [],
            resources: originalLesson.resources || [],
            practiceExercises: originalLesson.practiceExercises || [],
            professions: originalLesson.professions || [],
            videoUrl: originalLesson.videoUrl || '',
            isDraft: originalLesson.isDraft === undefined ? false : originalLesson.isDraft,
            image: originalLesson.image || '',
            imageFile: originalLesson.imageFile || null,
            imagePreview: originalLesson.imagePreview || null,
            moduleId: moduleId,
            courseId: courseId
          };

          // Если урок имеет _id (выбран из библиотеки), сохраняем его
          if (originalLesson._id) {
            lessonData._id = originalLesson._id;
          }

          let lessonId;
          try {
            const hasImageToUpload = lessonData.imageFile || (lessonData.imagePreview && !lessonData.image);

            let response;

            // Если урок существующий (выбран из библиотеки), обновляем его
            if (originalLesson._id) {
              lessonId = originalLesson._id;

              if (hasImageToUpload) {
                // Обновляем урок с изображением
                const formData = new FormData();

                const lessonDataForUpload = {
                  title: lessonData.title || '',
                  content: lessonData.content || '',
                  duration: Number(lessonData.duration) || 0,
                  type: lessonData.type || 'text',
                  contentType: lessonData.contentType || 'standard',
                  videoUrl: lessonData.videoUrl || '',
                  isDraft: lessonData.isDraft || false,
                  contentBlocks: lessonData.contentBlocks || [],
                  resources: lessonData.resources || [],
                  practiceExercises: lessonData.practiceExercises || [],
                  professions: lessonData.professions || [],
                  moduleId: moduleId,  // Привязка к модулю через moduleId
                  courseId: courseId
                };

                formData.append('data', JSON.stringify(lessonDataForUpload));

                // Добавляем изображение
                if (lessonData.imageFile) {
                  formData.append('image', lessonData.imageFile);
                } else if (lessonData.imagePreview) {
                  const imageBlob = await fetch(lessonData.imagePreview).then(r => r.blob());
                  formData.append('image', imageBlob, 'lesson-image.jpg');
                }

                response = await lessonsAPI.updateWithImage(lessonId, formData);
              } else {
                // Обновляем урок без изображения
                response = await lessonsAPI.update(lessonId, lessonData);
              }

              console.log(`      ✓ Существующий урок обновлен с ID: ${lessonId}`);
            } else {
              // Создаем новый урок
              if (hasImageToUpload) {
                // Создаем урок с изображением
                const formData = new FormData();

                const lessonDataForUpload = {
                  title: lessonData.title || '',
                  content: lessonData.content || '',
                  duration: Number(lessonData.duration) || 0,
                  type: lessonData.type || 'text',
                  contentType: lessonData.contentType || 'standard',
                  videoUrl: lessonData.videoUrl || '',
                  isDraft: lessonData.isDraft || false,
                  contentBlocks: lessonData.contentBlocks || [],
                  resources: lessonData.resources || [],
                  practiceExercises: lessonData.practiceExercises || [],
                  professions: lessonData.professions || [],
                  moduleId: moduleId,  // Привязка к модулю через moduleId
                  courseId: courseId
                };

                formData.append('data', JSON.stringify(lessonDataForUpload));

                // Добавляем изображение
                if (lessonData.imageFile) {
                  formData.append('image', lessonData.imageFile);
                } else if (lessonData.imagePreview) {
                  const imageBlob = await fetch(lessonData.imagePreview).then(r => r.blob());
                  formData.append('image', imageBlob, 'lesson-image.jpg');
                }

                response = await lessonsAPI.createWithImage(formData);
              } else {
                // Создаем урок без изображения
                response = await lessonsAPI.create(lessonData);
              }

              // Пробуем разные варианты получения ID из ответа
              lessonId = response.data.data?._id || response.data._id || response.data.lesson?._id || response.data.data?.id;

              if (!lessonId) {
                console.error('Структура ответа сервера при создании урока:', response.data);
                throw new Error('Не получен ID созданного урока из ответа сервера');
              }

              console.log(`      ✓ Урок создан с ID: ${lessonId}`);
            }
          } catch (error) {
            console.error(`      ✗ Ошибка при создании/обновлении урока "${originalLesson.title}":`, error);
            toast.error(`Ошибка при создании урока "${originalLesson.title}"`);
            totalErrors++;
            continue; // Переходим к следующему уроку
          }

          // 2.3. Для этого урока создаем тесты последовательно
          const lessonTests = originalLesson.tests || [];

          if (lessonTests.length > 0) {
            console.log(`      🧪 Создаем ${lessonTests.length} тестов для урока...`);

            for (let testIndex = 0; testIndex < lessonTests.length; testIndex++) {
              const test = lessonTests[testIndex];

              console.log(`        📝 Тест ${testIndex + 1}/${lessonTests.length}: "${test.title}"`);

              const testData = {
                ...(test._id && !test._id.startsWith('temp-test') && { _id: test._id }),
                lessonId: lessonId,
                title: test.title,
                description: test.description || '',
                timeLimit: Number(test.timeLimit) || 15,
                passingScore: Number(test.passingScore) || 70,
                attempts: Number(test.attempts) || 3,
                questions: test.questions.map((q) => ({
                  ...q,
                  points: Number(q.points) || 1,
                  type: q.type || 'single-choice',
                })),
              };

              try {
                if (testData._id) {
                  await testsAPI.update(testData._id, testData);
                  console.log(`          ✓ Тест обновлен`);
                } else {
                  await testsAPI.create(testData);
                  console.log(`          ✓ Тест создан`);
                }
              } catch (error) {
                console.error(`          ✗ Ошибка при создании теста "${test.title}":`, error);
                totalWarnings++;  // Тест не критичен, считаем как предупреждение
                // Не показываем toast для каждого теста, чтобы не спамить
                // Продолжаем со следующим тестом
              }
            }
          }
        }

        console.log(`  ✓ Модуль "${originalModule.title}" и его содержимое успешно созданы\n`);
      }

      // Показываем итоговое сообщение в зависимости от результатов
      if (totalErrors > 0) {
        console.log(`⚠️ Создание завершено с ошибками: ${totalErrors} критических ошибок`);
        toast.warning(`Курс создан, но с ${totalErrors} ошибками. Проверьте консоль для деталей.`);
      } else if (totalWarnings > 0) {
        console.log(`⚠️ Создание завершено с предупреждениями: ${totalWarnings} тестов не созданы`);
        toast.warning(`Курс создан, но ${totalWarnings} тестов не удалось создать. Вы можете добавить их позже.`);
      } else {
        console.log('✅ Все модули, уроки и тесты успешно созданы!');
        toast.success(t('courses:courseCreate.toasts.courseCreated'));
      }

      // Перенаправление на страницу нового курса по slug
      setTimeout(() => {
        navigate(`/courses/${courseSlug || courseId}`);
      }, 2000);
    } catch (err) {
      console.error('Ошибка при создании курса:', err);
      console.error('Ответ сервера:', err.response?.data);
      toast.error(
        err.response?.data?.message ||
          err.message ||
          t('courses:courseCreate.toasts.createError')
      );
    } finally {
      setLoading(false);
    }
  };

  // Обработчик импорта JSON
  const handleJsonImport = (jsonData) => {
    try {
      // Проверяем, что jsonData содержит нужные поля
      if (!jsonData.title || !jsonData.description) {
        toast.error(t('courses:courseCreate.toasts.jsonMissingFields'));
        return;
      }

      // Структура импортируемых данных
      let importData = {};

      // Если импортируем полный курс с course-объектом
      if (jsonData.course) {
        // Извлекаем основные данные курса
        importData = {
          title: jsonData.course.title,
          description: jsonData.course.description,
          difficulty: jsonData.course.difficulty,
          image: jsonData.course.image || '',
          modules: [],
        };

        // Обрабатываем модули, уроки и тесты
        if (jsonData.course.modules && Array.isArray(jsonData.course.modules)) {
          importData.modules = jsonData.course.modules.map((module) => {
            const processedModule = {
              title: module.title || '',
              description: module.description || '',
              content: module.content || '',
              order: module.order || 0,
              lessons: [],
              tests: [],
            };

            // Обрабатываем уроки
            if (module.lessons && Array.isArray(module.lessons)) {
              processedModule.lessons = module.lessons.map((lesson) => ({
                title: lesson.title || '',
                content: lesson.content || '',
                duration: Number(lesson.duration) || 15,
                type: lesson.type || 'text',
                contentType: lesson.contentType || 'standard',
                contentBlocks: lesson.contentBlocks || [],
                resources: lesson.resources || [],
                practiceExercises: lesson.practiceExercises || [],
                videoUrl: lesson.videoUrl || '',
              }));
            }

            // Обрабатываем тесты
            if (module.tests && Array.isArray(module.tests)) {
              processedModule.tests = module.tests.map((test) => ({
                title: test.title || '',
                description: test.description || '',
                timeLimit: Number(test.timeLimit) || 15,
                passingScore: Number(test.passingScore) || 70,
                attempts: Number(test.attempts) || 3,
                questions: Array.isArray(test.questions)
                  ? test.questions.map((q) => ({
                      ...q,
                      points: Number(q.points) || 1,
                      type: q.type || 'single-choice',
                    }))
                  : [],
              }));
            }

            return processedModule;
          });
        }
      } else {
        // Иначе просто импортируем данные как есть, но проверяем и преобразуем структуру
        importData = { ...jsonData };

        // Проверяем модули
        if (importData.modules && Array.isArray(importData.modules)) {
          importData.modules = importData.modules.map((module) => {
            const processedModule = { ...module };

            // Проверяем уроки
            if (module.lessons && Array.isArray(module.lessons)) {
              processedModule.lessons = module.lessons.map((lesson) => ({
                ...lesson,
                duration: Number(lesson.duration) || 15,
                type: lesson.type || 'text',
                contentType: lesson.contentType || 'standard',
                contentBlocks: lesson.contentBlocks || [],
                resources: lesson.resources || [],
                practiceExercises: lesson.practiceExercises || [],
              }));
            }

            // Проверяем тесты
            if (module.tests && Array.isArray(module.tests)) {
              processedModule.tests = module.tests.map((test) => ({
                ...test,
                timeLimit: Number(test.timeLimit) || 15,
                passingScore: Number(test.passingScore) || 70,
                attempts: Number(test.attempts) || 3,
                questions: Array.isArray(test.questions)
                  ? test.questions.map((q) => ({
                      ...q,
                      points: Number(q.points) || 1,
                      type: q.type || 'single-choice',
                    }))
                  : [],
              }));
            }

            return processedModule;
          });
        }
      }

      // Устанавливаем данные курса
      setCourseData(importData);
      toast.success(t('courses:courseCreate.toasts.jsonImported'));
    } catch (error) {
      console.error('Ошибка импорта JSON:', error);
      toast.error(t('courses:courseCreate.toasts.jsonImportError') + error.message);
    }
  };

  // Добавляем функцию для открытия диалога выбора уроков
  const handleOpenLessonSelector = (moduleIndex) => {
    setCurrentModuleIndex(moduleIndex);
    setLessonSelectorOpen(true);
  };

  // Добавляем функцию для обработки выбора уроков
  const handleSelectLessons = async (selectedLessons) => {
    if (currentModuleIndex === null) return;

    try {
      // Извлекаем ID уроков из выбранных объектов
      const lessonIds = selectedLessons.map(lesson =>
        typeof lesson === 'object' ? (lesson.slug) : lesson
      );

      // Загружаем полные данные выбранных уроков с сервера (включая тесты)
      const lessonPromises = lessonIds.map(lessonSlug =>
        lessonsAPI.getBySlug(lessonSlug)
      );
      const lessonResponses = await Promise.all(lessonPromises);
      const fullLessonsData = lessonResponses.map(response => response.data);

      // Получаем текущие модули
      const updatedModules = [...courseData.modules];

      // Добавляем загруженные уроки в текущий модуль с правильной структурой
      fullLessonsData.forEach(lesson => {
        // Проверяем, не добавлен ли уже этот урок
        const isLessonAlreadyAdded = updatedModules[currentModuleIndex].lessons.some(
          existingLesson => existingLesson._id === lesson._id
        );

        if (!isLessonAlreadyAdded) {
          // Формируем правильную структуру урока с тестами
          const formattedLesson = {
            _id: lesson._id,
            title: lesson.title || '',
            description: lesson.description || '',
            content: lesson.content || '',
            duration: lesson.duration || 15,
            type: lesson.type || 'text',
            contentType: lesson.contentType || 'standard',
            contentBlocks: lesson.contentBlocks || [],
            resources: lesson.resources || [],
            practiceExercises: lesson.practiceExercises || [],
            professions: lesson.professions || [],
            skills: lesson.skills || [],
            videoUrl: lesson.videoUrl || '',
            image: lesson.image || '',
            isDraft: lesson.isDraft || false,
            tests: lesson.tests || [] // Тесты загружаются с сервера!
          };

          updatedModules[currentModuleIndex].lessons.push(formattedLesson);
        }
      });

      // Обновляем состояние курса
      setCourseData(prevData => ({
        ...prevData,
        modules: updatedModules
      }));

      toast.success(`Добавлено ${fullLessonsData.length} уроков в модуль`);
    } catch (error) {
      console.error('Ошибка при добавлении уроков:', error);
      toast.error('Ошибка при добавлении уроков');
    }
  };

  return (
    <Container maxWidth={false} sx={{ maxWidth: '1400px' }}>
      <Box sx={{ my: 4 }}>
        {/* Breadcrumb Navigation */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography
              variant="body2"
              component="a"
              href="/courses"
              sx={{
                color: 'text.secondary',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              {t('courses:courseCreate.ui.backToCourses')}
            </Typography>
            <Typography variant="body2" color="text.secondary">/</Typography>
            <Typography variant="body2" fontWeight="600">
              {t('courses:courseCreate.ui.pageTitle')}
            </Typography>
          </Stack>
        </Box>

        {/* Header with Title and Save Button */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={2}
          mb={4}
        >
          <Typography variant="h3" component="h1" fontWeight="700">
            {t('courses:courseCreate.ui.pageTitle')}
          </Typography>
          <Stack direction="row" spacing={2}>
            <JsonImportExport
              data={courseData}
              onImport={handleJsonImport}
              title={t('courses:courseCreate.ui.jsonImportExport.title')}
              description={t('courses:courseCreate.ui.jsonImportExport.description')}
              jsonStructure={{
                title: 'Название курса',
                description: 'Описание курса',
                difficulty: 'Уровень сложности (beginner, intermediate, advanced)',
                modules: []
              }}
            />
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
              size="large"
            >
              {loading ? t('courses:courseCreate.ui.creating') : t('courses:courseCreate.ui.createCourseButton')}
            </Button>
          </Stack>
        </Stack>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={4}>
            {/* Main Content - 7 columns */}
            <Grid item xs={12} lg={7}>
              <Card
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                  overflow: 'visible' // Разрешаем dropdown выходить за пределы карточки
                }}
              >
                <Stack spacing={3}>
                  <TextField
                    fullWidth
                    required
                    label={t('courses:courseCreate.ui.courseTitle')}
                    name="title"
                    value={courseData.title}
                    onChange={handleCourseDataChange}
                    placeholder="e.g., Dropbox Fundamentals"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3
                      }
                    }}
                  />

                  <TextField
                    fullWidth
                    required
                    multiline
                    rows={5}
                    label={t('courses:courseCreate.ui.courseDescription')}
                    name="description"
                    value={courseData.description}
                    onChange={handleCourseDataChange}
                    placeholder="Enter the full description of the course..."
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3
                      }
                    }}
                  />

                  {/* Difficulty Level */}
                  <Box>
                    <Typography variant="body2" fontWeight={500} mb={1}>
                      {t('courses:courseCreate.ui.difficulty')}
                    </Typography>
                    <Select
                      fullWidth
                      name="difficulty"
                      value={courseData.difficulty}
                      onChange={handleCourseDataChange}
                      required
                      sx={{
                        borderRadius: 3,
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderRadius: 3
                        }
                      }}
                    >
                      <MenuItem value="beginner">
                        {t('courses:courseCreate.ui.difficultyLevels.beginner')}
                      </MenuItem>
                      <MenuItem value="intermediate">
                        {t('courses:courseCreate.ui.difficultyLevels.intermediate')}
                      </MenuItem>
                      <MenuItem value="advanced">
                        {t('courses:courseCreate.ui.difficultyLevels.advanced')}
                      </MenuItem>
                    </Select>
                  </Box>

                  {/* (Tools) Section */}
                  <CustomAutocomplete
                    label={t('courses:courseCreate.ui.tools')}
                    placeholder="Select tools"
                    options={tools}
                    loading={toolsLoading}
                    onChange={handleToolsChange}
                    getOptionLabel={(option) =>
                      option.term_group?.MainTerm?.value ||
                      option.library?.name ||
                      option.name ||
                      ''
                    }
                    selectedChips={selectedTools}
                    onChipDelete={(index) => {
                      const newTools = selectedTools.filter((_, i) => i !== index);
                      handleToolsChange(newTools);
                    }}
                    chipColor="default"
                    zIndex={1}
                  />

                  {/* Professions Section with Custom Autocomplete */}
                  <CustomAutocomplete
                    label={t('courses:courseCreate.ui.professions')}
                    placeholder={t('courses:coursesPage.filters.selectProfessions')}
                    value={selectedProfessions}
                    options={professions}
                    loading={professionsLoading}
                    onChange={handleProfessionsChange}
                    getOptionLabel={(option) => {
                      if (typeof option === 'string') return option;
                      return option.term_group?.MainTerm?.value ||
                             option.term_group?.name ||
                             option.name ||
                             option.title ||
                             '';
                    }}
                    selectedChips={selectedProfessions}
                    onChipDelete={(index) => {
                      const profToDelete = selectedProfessions[index];
                      const profId = String(profToDelete.id || profToDelete._id);
                      const newProfs = selectedProfessions.filter((_, i) => i !== index);
                      handleProfessionsChange(newProfs);
                      setManualProfessions(prevManual => {
                        const updated = new Set(prevManual);
                        updated.delete(profId);
                        return updated;
                      });
                    }}
                    getChipColor={(item) => {
                      const profId = String(item.id || item._id);
                      return manualProfessions.has(profId) ? 'secondary' : 'primary';
                    }}
                    chipColor="primary"
                    zIndex={1}
                  />

                  <Alert severity="info">
                    {t('courses:courseCreate.ui.professionsAutoSync')}
                  </Alert>
                </Stack>
              </Card>
            </Grid>

            {/* Sidebar - 5 columns */}
            <Grid item xs={12} lg={5}>
              <Stack spacing={3}>
                {/* Status Card */}
                <Card
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    overflow: 'visible' // Разрешаем dropdown выходить за пределы карточки
                  }}
                >
                  <Typography variant="body2" fontWeight={500} mb={2}>
                    Status
                  </Typography>
                  <Box sx={{ position: 'relative' }}>
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();
                        setStatusDropdownOpen(!statusDropdownOpen);
                      }}
                      sx={({ palette }) => ({
                        p: '0 16px',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 3,
                        bgcolor: palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        height: '48px',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: 'primary.main',
                          bgcolor: palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                        }
                      })}
                    >
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          fontSize: '0.875rem',
                          color: 'text.primary',
                          textTransform: 'capitalize' 
                        }}
                      >
                        {isDraft ? t('common:status.draft') : t('common:status.published')}
                      </Typography>
                      <KeyboardArrowDownIcon sx={{ 
                        fontSize: 20, 
                        color: 'text.secondary',
                        transition: 'transform 0.2s',
                        transform: statusDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                      }} />
                    </Box>
                    {statusDropdownOpen && (
                      <Box
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          mt: 1,
                          bgcolor: 'background.paper',
                          borderRadius: 3,
                          border: '1px solid',
                          borderColor: 'divider',
                          boxShadow: 3,
                          zIndex: 3000,
                          overflow: 'hidden'
                        }}
                      >
                        <Box sx={{ p: 1 }}>
                          <Box
                            onClick={() => {
                              setIsDraft(true);
                              setStatusDropdownOpen(false);
                            }}
                            sx={{
                              p: 1.5,
                              cursor: 'pointer',
                              borderRadius: 2,
                              '&:hover': {
                                bgcolor: 'action.hover'
                              }
                            }}
                          >
                            <Typography variant="body2">
                              {t('common:status.draft')}
                            </Typography>
                          </Box>
                          {(user?.userMeta?.roles?.includes('admin') || user?.userMeta?.roles?.includes('teacher')) && (
                            <Box
                              onClick={() => {
                                setIsDraft(false);
                                setStatusDropdownOpen(false);
                              }}
                              sx={{
                                p: 1.5,
                                cursor: 'pointer',
                                borderRadius: 2,
                                '&:hover': {
                                  bgcolor: 'action.hover'
                                }
                              }}
                            >
                              <Typography variant="body2">
                                {t('common:status.published')}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Card>

                {/* Hero Section Background Card */}
                <Card
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                  }}
                >
                  <Typography variant="body2" fontWeight={500} mb={2}>
                    Hero Section Background
                  </Typography>
                  <Box
                    sx={{
                      border: '2px dashed',
                      borderColor: 'divider',
                      borderRadius: 3,
                      p: 3,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: 'action.hover'
                      }
                    }}
                    onClick={() => document.getElementById('course-image-upload').click()}
                  >
                    {courseData.imagePreview || courseData.image ? (
                      <img
                        src={courseData.imagePreview || courseData.image}
                        alt="Preview"
                        style={{
                          width: '100%',
                          height: 'auto',
                          maxHeight: '200px',
                          objectFit: 'cover',
                          borderRadius: '12px'
                        }}
                      />
                    ) : (
                      <>
                        <AddIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                          Click to upload
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          PNG, JPG (1280x485px)
                        </Typography>
                      </>
                    )}
                  </Box>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="course-image-upload"
                    type="file"
                    onChange={handleImageUpload}
                  />
                  {(courseData.imagePreview || courseData.image) && (
                    <Button
                      fullWidth
                      variant="outlined"
                      color="error"
                      onClick={handleRemoveImage}
                      sx={{ mt: 2 }}
                    >
                      {t('courses:courseCreate.ui.removeImage')}
                    </Button>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
                    <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                    <Typography variant="caption" color="text.secondary" sx={{ mx: 1 }}>
                      OR
                    </Typography>
                    <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Paste image URL..."
                    name="image"
                    value={courseData.image}
                    onChange={handleCourseDataChange}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }}
                  />
                </Card>

                {/* Main Video URL Card */}
                <Card
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                  }}
                >
                  <Typography variant="body2" fontWeight={500} mb={2}>
                    Main Video URL
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Paste video URL..."
                    name="videoUrl"
                    value={courseData.videoUrl}
                    onChange={handleVideoUrlChange}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3
                      }
                    }}
                  />
                </Card>

                {/* Hero Preview Button */}
                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  onClick={() => setHeroPreviewOpen(!heroPreviewOpen)}
                  startIcon={heroPreviewOpen ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  sx={{
                    borderRadius: 3,
                    textTransform: 'none',
                    py: 1.5,
                    borderWidth: 2,
                    fontWeight: 600,
                    backgroundColor: 'transparent',
                    color: 'primary.main',
                    borderColor: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      borderColor: 'primary.main',
                      color: 'primary.main',
                    },
                  }}
                >
                  {heroPreviewOpen ? 'Hide Hero Preview' : 'Show Hero Preview'}
                </Button>
              </Stack>
            </Grid>

            {/* Модули, уроки и тесты — ниже, на всю ширину */}
            <Grid item xs={12}>
              {/* Hero Preview (if open) - Full Width, НАД модулями */}
              {heroPreviewOpen && (
                <Box sx={{ 
                  width: '100%', 
                  mb: 4,
                  borderRadius: 3,
                  overflow: 'hidden'
                }}>
                  <HeroSection
                    course={getPreviewCourseData()}
                    courseStats={calculatePreviewStats()}
                    isEnrolled={false}
                    isBookmarked={false}
                    onToggleBookmark={handleHeroPreviewToggleBookmark}
                    onContinueLearning={handleHeroPreviewContinueLearning}
                    onEnroll={handleHeroPreviewEnroll}
                    formatDuration={formatDuration}
                    onScrollToReviews={handleHeroPreviewScrollToReviews}
                    userRating={0}
                    onRatingSubmit={() => {}}
                  />
                </Box>
              )}

              {/* Header with title and buttons */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 3
              }}>
                <Typography variant="h5" component="h2">
                  {t('courses:courseCreate.ui.modulesAndLessons')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<LibraryAddIcon />}
                    onClick={() => setModuleSelectorOpen(true)}
                    color="secondary"
                  >
                    {t('courses:courseCreate.ui.addExistingModule')}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={addModule}
                  >
                    {t('courses:courseCreate.ui.addModule')}
                  </Button>
                </Box>
              </Box>
              
              {/* Если модулей нет, показываем пустое состояние */}
              {courseData.modules.length === 0 ? (
                <Card sx={{ mb: 4, p: 4, textAlign: 'center' }}>
                  <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                    {t('courses:courseCreate.ui.noModulesTitle')}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {t('courses:courseCreate.ui.noModulesDescription')}
                  </Typography>
                </Card>
              ) : (
                <>
                  {courseData.modules.map((module, moduleIndex) => {
                const moduleRef = { module }; // Обертываем модуль в структуру moduleRef
                
                return (
                  <ModuleCard
                    key={moduleIndex}
                    moduleRef={moduleRef}
                    moduleIndex={moduleIndex}
                    totalModules={courseData.modules.length}
                    isExpanded={expandedModules[moduleIndex] || false}
                    localModuleFields={localModuleFields}
                    user={user}
                    isDraft={module.isDraft || false}
                    showOrderField={true}
                    onToggleExpanded={handleToggleExpanded}
                    onModuleFieldChange={handleModuleFieldChange}
                    onRemoveModule={handleRemoveModule}
                    onAddLesson={handleAddLesson}
                    onOpenLessonSelector={handleOpenLessonSelector}
                    onLessonChange={handleLessonChange}
                    onModuleOrderChange={handleModuleOrderChange}
                    onProfessionsSync={syncProfessionsFromLessons}
                    onDraftToggle={(moduleIndex, isDraft) => {
                      setCourseData(prev => {
                        const updatedModules = [...prev.modules];
                        updatedModules[moduleIndex] = {
                          ...updatedModules[moduleIndex],
                          isDraft
                        };
                        return { ...prev, modules: updatedModules };
                      });
                    }}
                  />
                );
              })}
                </>
              )}
            </Grid>

            {/* Bottom Action Bar */}
            <Grid item xs={12}>
              <Box 
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  pt: 3
                }}
              >
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={loading}
                  size="large"
                >
                  {loading ? t('courses:courseCreate.ui.creating') : t('courses:courseCreate.ui.createCourseButton')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Box>

      {/* В конце компонента добавляем компонент LessonSelector */}
      <LessonSelector
        open={lessonSelectorOpen}
        onClose={() => setLessonSelectorOpen(false)}
        onSelectLessons={handleSelectLessons}
        selectedLessonIds={
          currentModuleIndex !== null && courseData.modules[currentModuleIndex]
            ? courseData.modules[currentModuleIndex].lessons.map(lesson => lesson._id)
            : []
        }
      />
      
      {/* Компонент для выбора существующих модулей */}
      <ModuleSelector
        open={moduleSelectorOpen}
        onClose={() => setModuleSelectorOpen(false)}
        onSelectModules={handleSelectExistingModules}
      />
    </Container>
  );
};

export default CourseCreate;

